import { useState } from 'react';

const services = [
  {
    id: 'sourcing',
    number: '01',
    icon: 'ri-search-2-line',
    color: 'bg-emerald-700',
    accentColor: 'border-emerald-600',
    tagColor: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    title: 'Approvisionnement stratégique & Intelligence fournisseurs',
    summary: 'Identifier et qualifier les partenaires d\'approvisionnement les plus fiables, conformes et collaboratifs au monde pour vos besoins techniques spécifiques.',
    items: [
      {
        label: 'Identification ciblée',
        detail: 'Repérer des sources mondiales réputées et de haute qualité qui correspondent à vos exigences techniques spécifiques. Nous exploitons un vaste réseau international couvrant l\'Europe, la Chine, l\'Inde et l\'Amérique du Nord pour identifier des options inaccessibles par les canaux d\'approvisionnement génériques.',
      },
      {
        label: 'Vérification rigoureuse',
        detail: 'Sélection en plusieurs étapes des fournisseurs potentiels pour n\'isoler que les options les plus viables, stables et collaboratives. Notre processus évalue la stabilité financière, la capacité de production, les systèmes qualité et le potentiel de partenariat à long terme.',
      },
      {
        label: 'Pré-qualification des installations',
        detail: 'Évaluation sur site et à distance des installations, des spécifications produits, et sollicitation d\'échantillons physiques et de dossiers techniques. Nous validons les environnements de fabrication selon vos normes internes et les cadres réglementaires applicables avant tout engagement.',
      },
    ],
    image: 'https://public.readdy.ai/ai/img_res/aed1e136945af81ccbd2810ff569c07b.jpg',
  },
  {
    id: 'compliance',
    number: '02',
    icon: 'ri-shield-check-line',
    color: 'bg-teal-700',
    accentColor: 'border-teal-600',
    tagColor: 'bg-teal-50 text-teal-700 border-teal-100',
    title: 'Conformité réglementaire & Assurance qualité',
    summary: 'S\'assurer que chaque fournisseur, produit et processus répond aux exigences strictes des organismes réglementaires mondiaux — de la FDA à Santé Canada en passant par l\'EMA.',
    items: [
      {
        label: 'Audit de conformité',
        detail: 'Évaluation détaillée de l\'alignement des produits et des installations avec les normes réglementaires mondiales, notamment FDA 21 CFR, BPF Santé Canada, directives EMA et exigences ICH. Nous identifions les lacunes avant qu\'elles ne deviennent des risques.',
      },
      {
        label: 'Représentation DMF canadienne',
        detail: 'Agir en tant qu\'agent localisé du Drug Master File (DMF) pour le marché canadien. Nous gérons la préparation, la soumission et la maintenance continue des dossiers DMF auprès de Santé Canada au nom des fabricants internationaux.',
      },
      {
        label: 'Coordination des audits',
        detail: 'Gestion transparente des audits internes des clients et des inspections réglementaires externes. Nous préparons les fournisseurs aux audits, coordonnons la logistique, fournissons un soutien à la traduction et assurons le suivi de toutes les actions correctives.',
      },
      {
        label: 'Gestion de la documentation',
        detail: 'Collecte et vérification complètes de toute la documentation technique et de sécurité — incluant CoA, CoC, données de stabilité, profils d\'impuretés et dossiers réglementaires — assurant une traçabilité complète pour chaque livraison.',
      },
    ],
    image: 'https://readdy.ai/api/search-image?query=pharmaceutical%20quality%20assurance%20laboratory%20scientist%20reviewing%20compliance%20documents%20regulatory%20standards%20clean%20professional%20environment%20white%20lab%20coat%20modern%20facility%20with%20green%20accents%20and%20stone%20tones&width=700&height=420&seq=svc-compliance-01&orientation=landscape',
  },
  {
    id: 'liaison',
    number: '03',
    icon: 'ri-user-voice-line',
    color: 'bg-stone-700',
    accentColor: 'border-stone-500',
    tagColor: 'bg-stone-100 text-stone-700 border-stone-200',
    title: 'Liaison exécutive & Protection des communications',
    summary: 'Agir en tant qu\'intermédiaire de confiance — protégeant votre temps, votre confidentialité et votre position stratégique tout au long de chaque interaction avec les fournisseurs.',
    items: [
      {
        label: 'Intermédiaire stratégique',
        detail: 'Nous agissons comme votre filtre principal, vous protégeant des communications fournisseurs excessives ou « abusives » et maintenant une confidentialité totale jusqu\'à ce que vous soyez prêt à procéder. Votre identité et vos intentions stratégiques restent protégées tout au long du processus de qualification.',
      },
      {
        label: 'Coordination transfrontalière',
        detail: 'Surmonter les barrières linguistiques et gérer la logistique complexe des fuseaux horaires pour une réactivité 24h/7j. Notre équipe bilingue (EN/FR) et notre réseau multilingue garantissent que rien ne se perd dans la traduction — que ce soit en mandarin, en hindi ou en allemand.',
      },
      {
        label: 'Représentation mondiale',
        detail: 'Représentation professionnelle des fournisseurs et coordination de réunions importantes, visites de sites internationaux et visites d\'usines. Nous veillons à ce que vos intérêts soient représentés avec autorité et précision à chaque point de contact.',
      },
    ],
    image: 'https://readdy.ai/api/search-image?query=professional%20executive%20business%20meeting%20international%20team%20around%20conference%20table%20global%20corporate%20environment%20clean%20modern%20office%20with%20warm%20stone%20tones%20and%20green%20plants%20representing%20cross-border%20collaboration&width=700&height=420&seq=svc-liaison-01&orientation=landscape',
  },
  {
    id: 'logistics',
    number: '04',
    icon: 'ri-truck-line',
    color: 'bg-emerald-800',
    accentColor: 'border-emerald-700',
    tagColor: 'bg-emerald-50 text-emerald-800 border-emerald-100',
    title: 'Logistique & Architecture contractuelle',
    summary: 'Obtenir les meilleures conditions possibles et gérer le parcours physique complet de vos marchandises — de l\'usine à vos installations.',
    items: [
      {
        label: 'Excellence en négociation',
        detail: 'Obtenir des prix optimaux et des conditions d\'approvisionnement favorables grâce à une architecture contractuelle experte. Nous exploitons une connaissance approfondie du marché et des relations fournisseurs pour négocier des conditions qui protègent vos marges et assurent la continuité de l\'approvisionnement.',
      },
      {
        label: 'Logistique de bout en bout',
        detail: 'Gestion de la planification, du transport et du dédouanement des marchandises à travers les frontières internationales. Nous coordonnons le fret à température contrôlée, le suivi IoT et la conformité réglementaire complète à chaque passage de frontière.',
      },
      {
        label: 'Planification de la chaîne d\'approvisionnement',
        detail: 'Coordination des calendriers de production et des exigences de livraison pour assurer des opérations lean et juste-à-temps. Nous alignons les délais des fournisseurs avec votre calendrier de production pour minimiser les coûts de stockage tout en éliminant le risque de rupture.',
      },
    ],
    image: 'https://readdy.ai/api/search-image?query=international%20cargo%20logistics%20shipping%20containers%20port%20aerial%20view%20global%20supply%20chain%20management%20warm%20golden%20hour%20light%20industrial%20atmosphere%20professional%20freight%20transport%20network%20emerald%20and%20stone%20color%20palette&width=700&height=420&seq=svc-logistics-01&orientation=landscape',
  },
];

export default function ServicesDetailFr() {
  const [activeService, setActiveService] = useState(services[0].id);
  const active = services.find((s) => s.id === activeService) ?? services[0];

  return (
    <section className="w-full bg-white py-20 px-6 md:px-16">
      <div className="max-w-7xl mx-auto">

        {/* Tab nav */}
        <div className="flex flex-wrap gap-2 mb-14 border-b border-stone-100 pb-6">
          {services.map((s) => (
            <button
              key={s.id}
              onClick={() => setActiveService(s.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeService === s.id
                  ? `${s.color} text-white`
                  : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
              }`}
            >
              <i className={`${s.icon} text-base`} />
              <span className="hidden sm:inline">{s.title.split('&')[0].trim()}</span>
              <span className="sm:hidden">{s.number}</span>
            </button>
          ))}
        </div>

        {/* Active service detail */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">

          {/* Left: text */}
          <div>
            <div className="flex items-center gap-4 mb-6">
              <div className={`w-12 h-12 flex items-center justify-center rounded-lg ${active.color}`}>
                <i className={`${active.icon} text-white text-2xl`} />
              </div>
              <div>
                <span className="text-xs font-bold text-stone-400 tracking-widest uppercase">Service {active.number}</span>
                <h2
                  className="text-2xl md:text-3xl font-extrabold text-stone-900 leading-tight"
                  style={{ fontFamily: "'DM Serif Display', serif" }}
                >
                  {active.title}
                </h2>
              </div>
            </div>

            <p className="text-stone-600 text-base leading-relaxed mb-8 border-l-4 border-emerald-500 pl-5 italic">
              {active.summary}
            </p>

            <div className="space-y-5">
              {active.items.map((item, i) => (
                <div key={i} className={`rounded-lg border-l-4 ${active.accentColor} bg-stone-50 px-5 py-4`}>
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-checkbox-circle-fill text-emerald-600 text-base" />
                    <h4 className="text-stone-800 font-bold text-sm">{item.label}</h4>
                  </div>
                  <p className="text-stone-500 text-sm leading-relaxed">{item.detail}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right: image + tags */}
          <div className="flex flex-col gap-6">
            <div className="w-full h-64 md:h-80 rounded-lg overflow-hidden">
              <img
                src={active.image}
                alt={active.title}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {active.items.map((item, i) => (
                <span
                  key={i}
                  className={`text-xs font-semibold border px-3 py-1 rounded-full ${active.tagColor}`}
                >
                  {item.label}
                </span>
              ))}
            </div>
            <div className="bg-stone-50 rounded-lg border border-stone-100 p-5">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-information-line text-emerald-600" />
                <span className="text-xs font-bold uppercase tracking-widest text-stone-500">Pourquoi c&apos;est important</span>
              </div>
              <p className="text-stone-600 text-sm leading-relaxed">
                Dans l&apos;environnement mondial complexe d&apos;aujourd&apos;hui, chacune de ces capacités réduit directement les risques, protège les marges et garantit la résilience de votre chaîne d&apos;approvisionnement — quels que soient les changements géopolitiques ou réglementaires.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
